import { useParams, Link, Navigate } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  Home as HomeIcon,
  Layers,
  MessageCircle,
  Users,
  Sparkles,
} from "lucide-react";
import { SERVICES, whatsappUrl, HERO_VISUALS } from "@/lib/site-data";
import { SERVICE_META } from "@/lib/service-visuals";
import { SERVICE_EXTRAS } from "@/lib/service-extras";
import useSeo from "@/lib/useSeo";
import JsonLd from "@/lib/JsonLd";
import ServiceHeroDiagram from "@/components/ServiceHeroDiagram";
import ServiceWorkflow from "@/components/ServiceWorkflow";

const SERVICE_ORDER_PANEL_SLUG = "uye-isyerine-ozel-siparis-sistemi";

export default function ServiceDetail() {
  const { slug } = useParams();
  const svc = SERVICES.find((s) => s.slug === slug);

  useSeo({
    title: svc?.seoTitle || `${svc?.title || "Hizmet"} | Epersonel`,
    description: svc?.seoDescription || svc?.short || "Epersonel hizmet detayı.",
  });

  if (!svc) return <Navigate to="/hizmetlerimiz" replace />;

  const meta = SERVICE_META[svc.slug] || {
    Icon: Layers,
    category: "HİZMET",
    accent: "#00F22E",
  };
  const Icon = meta.Icon;
  const extras = SERVICE_EXTRAS[svc.slug] || {};
  const showOrderPanel = slug === SERVICE_ORDER_PANEL_SLUG;
  const related = SERVICES.filter((s) => s.slug !== svc.slug);

  return (
    <>
      <JsonLd
        id={`service-detail-breadcrumb-${svc.slug}`}
        data={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Ana Sayfa", item: "https://epersonel.biz/" },
            { "@type": "ListItem", position: 2, name: "Hizmetlerimiz", item: "https://epersonel.biz/hizmetlerimiz" },
            { "@type": "ListItem", position: 3, name: svc.title, item: `https://epersonel.biz/hizmetlerimiz/${svc.slug}` },
          ],
        }}
      />

      {/* HERO */}
      <section className="relative overflow-hidden pt-10 md:pt-16 pb-10 md:pb-16">
        <div className="ep-glow-tl" aria-hidden="true" />
        <div className="ep-container relative">
          <nav
            aria-label="Sayfa Yolu"
            className="flex items-center gap-2 text-xs text-white/60 mb-6 md:mb-8"
          >
            <Link to="/" className="hover:text-[#00F22E] inline-flex items-center gap-1">
              <HomeIcon className="h-3 w-3" /> Ana Sayfa
            </Link>
            <span>/</span>
            <Link to="/hizmetlerimiz" className="hover:text-[#00F22E]">
              Hizmetlerimiz
            </Link>
            <span>/</span>
            <span className="text-white/80">{svc.title}</span>
          </nav>

          <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,440px)] gap-10 lg:gap-14 items-center">
            <div>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 rounded-xl bg-[#00F22E]/10 text-[#00F22E] grid place-items-center shrink-0 ring-1 ring-[#00F22E]/25">
                  <Icon className="h-5 w-5" />
                </div>
                <span className="text-[10px] tracking-[0.3em] text-[#00F22E]/90 font-semibold">
                  {meta.category}
                </span>
              </div>
              {svc.eyebrow && (
                <p className="text-xs tracking-[0.3em] text-[#00F22E] font-medium mb-4">
                  {svc.eyebrow}
                </p>
              )}
              <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.08]">
                {svc.heading || svc.title}
              </h1>
              <p className="mt-6 text-white/80 text-base md:text-lg leading-relaxed max-w-xl">
                {svc.intro || svc.short}
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Link
                  to="/basvuru"
                  data-testid="service-hero-cta-primary"
                  className="btn-primary"
                >
                  Ücretsiz Analiz İste <ArrowRight className="h-4 w-4" />
                </Link>
                <a
                  href={whatsappUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="service-hero-cta-whatsapp"
                  className="btn-secondary"
                >
                  <MessageCircle className="h-4 w-4" />
                  WhatsApp&apos;tan Görüşelim
                </a>
              </div>
            </div>
            <div className="ep-svc-detail-hero-slot hidden lg:block" aria-hidden="true">
              <ServiceHeroDiagram slug={svc.slug} />
            </div>
          </div>
        </div>
      </section>

      {/* KEY STATEMENTS */}
      {(svc.mainStatement || svc.secondaryStatement || svc.highlight) && (
        <section className="pb-2">
          <div className="ep-container">
            <div className="grid md:grid-cols-3 gap-5">
              {svc.mainStatement && (
                <div className="ep-card p-6 h-full md:col-span-2">
                  <p className="font-display text-xl md:text-2xl font-semibold text-white leading-snug">
                    {svc.mainStatement}
                  </p>
                  {svc.secondaryStatement && (
                    <p className="mt-3 text-white/70 leading-relaxed">
                      {svc.secondaryStatement}
                    </p>
                  )}
                </div>
              )}
              {svc.highlight && (
                <div
                  className="ep-card p-6 h-full border-[#00F22E]/30 relative overflow-hidden"
                  style={{
                    background:
                      "radial-gradient(circle at 100% 0%, rgba(0,242,46,0.10), transparent 60%), #101511",
                  }}
                >
                  <p className="text-[10px] tracking-[0.3em] text-[#00F22E]/90 font-semibold mb-3">
                    ÖZET
                  </p>
                  <p className="font-display text-lg md:text-xl font-semibold text-[#00F22E] leading-snug">
                    {svc.highlight}
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* WORKFLOW — code-native animated diagram */}
      <ServiceWorkflow slug={svc.slug} />

      {/* AUDIENCE — Bu hizmet kimler için uygun? */}
      {extras.audience && (
        <section className="py-14 md:py-20">
          <div className="ep-container">
            <div className="max-w-3xl mb-10">
              <p className="text-xs tracking-[0.3em] text-[#00F22E]/90 font-semibold mb-3">
                UYUM
              </p>
              <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight leading-tight">
                Bu Hizmet <span className="text-[#00F22E]">Kimler İçin Uygun?</span>
              </h2>
            </div>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {extras.audience.map((a, i) => (
                <li
                  key={i}
                  className="ep-card p-5 flex items-start gap-3 h-full"
                  data-testid={`service-audience-${i}`}
                >
                  <Users className="h-5 w-5 text-[#00F22E] mt-0.5 shrink-0" />
                  <span className="text-sm md:text-[15px] text-white/90 leading-relaxed">
                    {a}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* WEB SİPARİŞ SİSTEMİ visual panel — only for that service */}
      {showOrderPanel && (
        <section className="py-12 md:py-16 ep-section-tier-2 border-y border-white/[0.05] relative overflow-hidden">
          <div className="ep-glow-br" aria-hidden="true" />
          <div className="ep-container relative grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div>
              <p className="text-xs tracking-[0.3em] text-[#00F22E] font-semibold mb-3">
                YÖNETİM PANELİ
              </p>
              <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight leading-tight">
                Web sipariş ve operasyonunuzu{" "}
                <span className="text-[#00F22E]">tek panelden yönetin.</span>
              </h2>
              <ul className="mt-6 space-y-2.5 text-sm md:text-base text-white/90">
                {[
                  "Web sitesi üzerinden sipariş alma",
                  "Ürün ve stok yönetimi",
                  "Sipariş yönetimi ve durum takibi",
                  "Satış raporları",
                  "Kurye takibi ve harita üzerinde canlı görüntüleme",
                ].map((f, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <CheckCircle2 className="h-5 w-5 text-[#00F22E] mt-0.5 shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-xs text-white/60 leading-relaxed">
                Görsel, sistemi tanıtmak amaçlı temsili bir gösterimdir; canlı
                bir müşteri ekran görüntüsü değildir.
              </p>
            </div>
            <div className="relative ep-fade-up aspect-[16/10]">
              <img
                src={HERO_VISUALS.desktopMonitor}
                alt="Emek Market web sipariş ve operasyon yönetim paneli"
                className="absolute inset-0 w-full h-full object-contain ep-hero-visual"
                loading="lazy"
                width="1600"
                height="1000"
                data-testid="service-panel-visual"
              />
              <img
                src={HERO_VISUALS.deskNotifNewOrder}
                alt="Yeni sipariş #1048 masaüstü bildirimi"
                className="absolute left-[-4%] top-[10%] md:top-[14%] w-[28%] max-w-[210px] h-auto ep-notif d1"
                loading="lazy"
                width="600"
                height="200"
                data-testid="service-panel-notif-1"
              />
              <img
                src={HERO_VISUALS.deskNotifCourier}
                alt="Kurye teslimata çıktı masaüstü bildirimi"
                className="absolute right-[-4%] bottom-[-4%] w-[30%] max-w-[225px] h-auto ep-notif d3"
                loading="lazy"
                width="600"
                height="200"
                data-testid="service-panel-notif-2"
              />
            </div>
          </div>
        </section>
      )}

      {svc.appStoreNote && (
        <section className="pt-8">
          <div className="ep-container">
            <div className="ep-card p-6 max-w-4xl mx-auto border-[#00F22E]/20">
              <p className="text-sm text-white/80 leading-relaxed">{svc.appStoreNote}</p>
            </div>
          </div>
        </section>
      )}

      {/* HOW WE PROCEED — Nasıl İlerliyoruz? (4 clear steps) */}
      {extras.steps && (
        <section className="py-16 md:py-20 ep-section-tier-2 border-y border-white/[0.05] relative overflow-hidden">
          <div className="ep-glow-br" aria-hidden="true" />
          <div className="ep-container relative">
            <div className="max-w-3xl mb-10">
              <p className="text-xs tracking-[0.3em] text-[#00F22E]/90 font-semibold mb-3">
                SÜREÇ
              </p>
              <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight leading-tight">
                Nasıl <span className="text-[#00F22E]">İlerliyoruz?</span>
              </h2>
            </div>
            <div className="ep-stage-row" data-testid="service-steps">
              {extras.steps.map((s, i) => (
                <div
                  key={i}
                  className="ep-stage"
                  style={{ animationDelay: `${i * 90}ms` }}
                >
                  <div className="ep-stage-node">
                    <span className="ep-stage-index">0{i + 1}</span>
                    <h3 className="ep-stage-title mt-3">{s.title}</h3>
                    <p className="ep-stage-desc">{s.desc}</p>
                  </div>
                  {i < extras.steps.length - 1 && (
                    <span className="ep-stage-connector" aria-hidden="true" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* BENEFITS — Neler kazanırsınız? */}
      {extras.benefits && (
        <section className="py-16 md:py-20">
          <div className="ep-container">
            <div className="max-w-3xl mb-10">
              <p className="text-xs tracking-[0.3em] text-[#00F22E]/90 font-semibold mb-3">
                KAZANIM
              </p>
              <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight leading-tight">
                Bu Hizmetle <span className="text-[#00F22E]">Neler Kazanırsınız?</span>
              </h2>
            </div>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {extras.benefits.map((b, i) => (
                <li
                  key={i}
                  className="ep-card p-5 flex items-start gap-3 h-full"
                  data-testid={`service-benefit-${i}`}
                >
                  <Sparkles className="h-5 w-5 text-[#00F22E] mt-0.5 shrink-0" />
                  <span className="text-sm md:text-[15px] text-white/90 leading-relaxed">
                    {b}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* PROCESS timeline — legacy detailed runtime flow (currently only for kurye) */}
      {svc.process && (
        <section className="py-16 md:py-20 ep-section-tier-2 border-y border-white/[0.05]">
          <div className="ep-container">
            <div className="max-w-3xl mb-10">
              <p className="text-xs tracking-[0.3em] text-[#00F22E]/90 font-semibold mb-3">
                AKIŞ
              </p>
              <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight leading-tight">
                Sipariş Nasıl <span className="text-[#00F22E]">Sonlanır?</span>
              </h2>
            </div>
            <ol className="ep-svc-process">
              {svc.process.map((p, i) => (
                <li
                  key={i}
                  className="ep-svc-process-item"
                  style={{ animationDelay: `${i * 90}ms` }}
                >
                  <span className="ep-svc-process-index">{i + 1}</span>
                  <p className="text-white/90 leading-relaxed">{p}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>
      )}

      {/* SCOPE / FEATURES */}
      {(svc.scope || svc.features) && (
        <section className="py-16 md:py-20 ep-section-tier-2 border-y border-white/[0.05] relative overflow-hidden">
          <div className="ep-glow-tl" aria-hidden="true" />
          <div className="ep-container relative">
            <div className="max-w-3xl mb-10">
              <p className="text-xs tracking-[0.3em] text-[#00F22E]/90 font-semibold mb-3">
                KAPSAM
              </p>
              <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight leading-tight">
                Hizmet <span className="text-[#00F22E]">Kapsamı</span>
              </h2>
            </div>
            <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {(svc.scope || svc.features).map((f, i) => (
                <li
                  key={i}
                  className="ep-card p-5 flex items-start gap-3 h-full"
                >
                  <CheckCircle2 className="h-5 w-5 text-[#00F22E] mt-0.5 shrink-0" />
                  <span className="text-sm md:text-[15px] text-white/90 leading-relaxed">
                    {f}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {svc.disclaimer && (
        <section className="pt-8">
          <div className="ep-container max-w-4xl">
            <p className="text-xs text-white/55 leading-relaxed">{svc.disclaimer}</p>
          </div>
        </section>
      )}

      {/* RELATED SERVICES */}
      {related.length > 0 && (
        <section className="py-16 md:py-20">
          <div className="ep-container">
            <div className="max-w-3xl mb-10">
              <p className="text-xs tracking-[0.3em] text-[#00F22E]/90 font-semibold mb-3">
                DEVAMI VAR
              </p>
              <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight leading-tight">
                İlgili Diğer <span className="text-[#00F22E]">Hizmetler</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {related.map((r) => {
                const rMeta = SERVICE_META[r.slug] || {
                  Icon: Layers,
                  category: "HİZMET",
                  accent: "#00F22E",
                };
                const RIcon = rMeta.Icon;
                return (
                  <Link
                    key={r.slug}
                    to={`/hizmetlerimiz/${r.slug}`}
                    data-testid={`related-service-${r.slug}`}
                    className="ep-svc-card group flex flex-col h-full"
                    style={{ "--svc-accent": rMeta.accent }}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-xl bg-[#00F22E]/10 text-[#00F22E] grid place-items-center shrink-0 ring-1 ring-[#00F22E]/25">
                        <RIcon className="h-5 w-5" />
                      </div>
                      <span className="text-[10px] tracking-[0.24em] text-[#00F22E]/80 font-semibold">
                        {rMeta.category}
                      </span>
                    </div>
                    <h3 className="font-display text-lg font-semibold tracking-tight leading-snug">
                      {r.title}
                    </h3>
                    <p className="mt-3 text-sm text-white/70 leading-relaxed flex-1">
                      {r.short}
                    </p>
                    <span className="mt-5 inline-flex items-center gap-1.5 text-sm text-[#00F22E] font-medium group-hover:gap-2.5 transition-[gap] duration-200">
                      {r.ctaLabel} <ArrowRight className="h-4 w-4" />
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* CLOSING CTA */}
      <section className="py-14 md:py-16 ep-section-tier-2 border-y border-white/[0.05] relative overflow-hidden">
        <div className="ep-glow-tl" aria-hidden="true" />
        <div className="ep-glow-br" aria-hidden="true" />
        <div className="ep-container relative">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight">
              {svc.closingHeading ||
                "İşyerinizin dijital satış potansiyelini birlikte açığa çıkaralım."}
            </h2>
            <p className="mt-5 text-white/80 text-base md:text-lg leading-relaxed">
              Mevcut yapınızı ve ihtiyacınızı inceleyerek işyerinize özel bir yol
              haritası oluşturalım.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/basvuru"
                className="btn-primary"
                data-testid="service-cta-primary"
              >
                {svc.closingCta || "Ücretsiz Analiz İste"}{" "}
                <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href={whatsappUrl()}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary"
                data-testid="service-cta-secondary"
              >
                <MessageCircle className="h-4 w-4" />
                {svc.secondaryCta || "WhatsApp'tan Görüşelim"}
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
